import React, { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import { useI18n } from "../../contexts/I18nContext";
import { api, resolveUrl, formatApiError } from "../../lib/api";
import { fmtDate } from "../../lib/dateFormat";
import { Car, Calendar, Plus, Trash2, CalendarPlus, CalendarMinus, Star, Ban, FileWarning, AlertTriangle, FileText, Pencil, Check, X, Camera, ImageIcon, LogIn, LogOut } from "lucide-react";
import { Routes, Route } from "react-router-dom";
import { DayPicker } from "react-day-picker";
import { format, parseISO } from "date-fns";
import PushNotificationToggle from "../../components/PushNotificationToggle";

const ownerLinks = (t) => [
  { to: "/owner", end: true, key: "cars", label: t("owner.myCars"), icon: <Car className="w-4 h-4" /> },
  { to: "/owner/reservations", key: "reservations", label: t("owner.reservations"), icon: <Calendar className="w-4 h-4" /> },
  { to: "/owner/documents", key: "documents", label: "Documente", icon: <FileWarning className="w-4 h-4" /> },
];

export default function OwnerDashboard() {
  const { t } = useI18n();
  return (
    <DashboardLayout title={t("owner.dashboard")} links={ownerLinks(t)}>
      <Routes>
        <Route index element={<OwnerCars />} />
        <Route path="reservations" element={<OwnerReservations />} />
        <Route path="documents" element={<OwnerDocuments />} />
      </Routes>
    </DashboardLayout>
  );
}

function OwnerCars() {
  const { t } = useI18n();
  const [cars, setCars] = useState([]);
  const [openCalendarFor, setOpenCalendarFor] = useState(null);

  useEffect(() => { api.get("/owner/cars").then((r) => setCars(r.data)); }, []);

  return (
    <div data-testid="owner-cars">
      <div className="mb-10">
        <div className="label-eyebrow mb-2" style={{ color: "var(--brand-primary)" }}>{t("owner.dashboard")}</div>
        <h1 className="font-serif text-3xl sm:text-4xl">{t("owner.myCars")}</h1>
      </div>
      <PushNotificationToggle />
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        {cars.map((c) => (
          <div key={c.id} className="card-surface p-6" data-testid={`owner-car-${c.id}`}>
            <div className="aspect-video overflow-hidden mb-4 bg-[var(--brand-surface-elevated)]">
              {c.images?.[0] ? <img src={resolveUrl(c.images[0].url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: "var(--brand-text-secondary)" }}>No image</div>}
            </div>
            <h3 className="font-serif text-xl mb-1">{c.name}</h3>
            <p className="text-xs mb-4" style={{ color: "var(--brand-text-secondary)" }}>{c.brand} &middot; €{c.price_eur}/zi</p>
            <button onClick={() => setOpenCalendarFor(c)} className="btn-ghost w-full flex items-center justify-center gap-2" data-testid={`open-calendar-${c.id}`}>
              <Calendar className="w-3.5 h-3.5" /> {t("owner.blockDates")}
            </button>
          </div>
        ))}
        {cars.length === 0 && <div className="md:col-span-3 text-center py-16 text-sm" style={{ color: "var(--brand-text-secondary)" }}>{t("owner.noCars")}</div>}
      </div>
      {openCalendarFor && (
        <CalendarDialog car={openCalendarFor} onClose={() => setOpenCalendarFor(null)} />
      )}
    </div>
  );
}

function CalendarDialog({ car, onClose }) {
  const { t } = useI18n();
  const [blocks, setBlocks] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [range, setRange] = useState();
  const [reason, setReason] = useState("");

  const load = async () => {
    const [b, a] = await Promise.all([
      api.get(`/cars/${car.id}/blocks`),
      api.get(`/cars/${car.id}/availability`),
    ]);
    setBlocks(b.data);
    setReservations(a.data.reserved_intervals);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!range?.from || !range?.to) return;
    try {
      await api.post(`/cars/${car.id}/blocks`, {
        car_id: car.id,
        start_date: format(range.from, "yyyy-MM-dd"),
        end_date: format(range.to, "yyyy-MM-dd"),
        reason,
      });
      setRange(undefined); setReason("");
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  const del = async (id) => {
    await api.delete(`/cars/${car.id}/blocks/${id}`);
    await load();
  };

  const disabledIntervals = [
    ...blocks.map((b) => ({ from: parseISO(b.start_date), to: parseISO(b.end_date) })),
    ...reservations.map((r) => ({ from: parseISO(r.start_date), to: parseISO(r.end_date) })),
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-auto p-3 sm:p-8" onClick={onClose} data-testid="calendar-dialog">
      <div className="card-surface p-5 sm:p-8 w-full max-w-3xl mt-4 sm:mt-8 max-h-[95vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl">{car.name} — {t("owner.blockDates")}</h2>
          <button onClick={onClose}>×</button>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <DayPicker mode="range" selected={range} onSelect={setRange} disabled={[{ before: new Date() }, ...disabledIntervals]} />
            <input className="brand-input mt-3" placeholder={t("owner.blockReason")} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="block-reason" />
            <button onClick={add} className="btn-gold-filled w-full mt-3" data-testid="add-block-btn">{t("owner.addBlock")}</button>
          </div>
          <div>
            <h3 className="label-eyebrow mb-3">Blocaje active</h3>
            <ul className="space-y-2 text-sm">
              {blocks.map((b) => (
                <li key={b.id} className="flex items-center justify-between card-surface-elevated p-3" data-testid={`block-${b.id}`}>
                  <div>
                    <div>{fmtDate(b.start_date)} → {fmtDate(b.end_date)}</div>
                    {b.reason && <div className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>{b.reason}</div>}
                  </div>
                  <button onClick={() => del(b.id)} className="btn-ghost"><Trash2 className="w-3.5 h-3.5" /></button>
                </li>
              ))}
              {blocks.length === 0 && <li className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>Niciun blocaj</li>}
            </ul>
            <h3 className="label-eyebrow mt-6 mb-3">Rezervari aprobate</h3>
            <ul className="space-y-2 text-sm">
              {reservations.map((r, i) => (
                <li key={i} className="card-surface-elevated p-3">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</li>
              ))}
              {reservations.length === 0 && <li className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>Nicio rezervare</li>}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function OwnerReservations() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [extendFor, setExtendFor] = useState(null);
  const [shortenFor, setShortenFor] = useState(null);
  const [newEndDate, setNewEndDate] = useState("");
  const [photoFor, setPhotoFor] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => setItems((await api.get("/owner/reservations")).data);
  useEffect(() => { load(); }, []);

  const submitExtend = async () => {
    try {
      await api.post(`/owner/reservations/${extendFor.id}/extend`, { new_end_date: newEndDate });
      setExtendFor(null); setNewEndDate("");
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };
  const submitShorten = async () => {
    try {
      await api.post(`/owner/reservations/${shortenFor.id}/shorten`, { new_end_date: newEndDate });
      setShortenFor(null); setNewEndDate("");
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };
  const cancel = async (id) => {
    if (!window.confirm("Anuleaza rezervarea?")) return;
    try { await api.post(`/owner/reservations/${id}/cancel`); await load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  const confirmPickup = async (id) => {
    if (!window.confirm("Confirmi ca ai predat masina clientului?")) return;
    try { await api.post(`/owner/reservations/${id}/confirm-pickup`); await load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  const confirmReturn = async (id) => {
    if (!window.confirm("Confirmi ca inchirierea s-a incheiat si masina a fost returnata?")) return;
    try { await api.post(`/owner/reservations/${id}/confirm-return`); await load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  const STATUS_LABEL = { pending: "In asteptare", approved: "Aprobata", in_progress: "In desfasurare", completed: "Incheiata", cancelled: "Anulata", rejected: "Respinsa" };
  const STATUS_COLOR = { approved: "var(--brand-primary)", in_progress: "#3b82f6", completed: "#22c55e", cancelled: "#ef4444", rejected: "#ef4444" };

  return (
    <div data-testid="owner-reservations">
      <div className="mb-10">
        <div className="label-eyebrow mb-2" style={{ color: "var(--brand-primary)" }}>{t("owner.dashboard")}</div>
        <h1 className="font-serif text-3xl sm:text-4xl">{t("owner.reservations")}</h1>
      </div>
      <div className="space-y-4">
        {items.map((r) => (
          <div key={r.id} className="card-surface p-6" data-testid={`owner-res-${r.id}`}>
            <div className="grid md:grid-cols-4 gap-4 text-sm mb-4">
              <div>
                <div className="label-eyebrow mb-1">Masina</div>
                {r.car_name}
              </div>
              <div>
                <div className="label-eyebrow mb-1">Client</div>
                {r.guest_name}
                <div className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>{r.guest_email}</div>
                <div className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>{r.guest_phone}</div>
              </div>
              <div>
                <div className="label-eyebrow mb-1">Perioada</div>
                {fmtDate(r.start_date)} → {fmtDate(r.end_date)}
                {r.extended_at && <div className="text-xs" style={{ color: "var(--brand-primary)" }}>prelungita</div>}
                {r.cancelled_at && <div className="text-xs" style={{ color: "#ef4444" }}>anulata</div>}
              </div>
              <div>
                <div className="label-eyebrow mb-1">{t("common.status")}</div>
                <span style={{ color: STATUS_COLOR[r.status] || "var(--brand-text-secondary)" }}>{STATUS_LABEL[r.status] || r.status}</span>
                {r.handed_over_at && <div className="text-xs mt-0.5" style={{ color: "var(--brand-text-secondary)" }}>Predata: {fmtDate(r.handed_over_at.slice(0,10))}</div>}
                {r.returned_at && <div className="text-xs mt-0.5" style={{ color: "var(--brand-text-secondary)" }}>Returnata: {fmtDate(r.returned_at.slice(0,10))}</div>}
              </div>
            </div>
            {(r.status === "approved" || r.status === "in_progress") && (
              <div className="flex flex-wrap gap-2 pt-3 border-t" style={{ borderColor: "var(--brand-border)" }}>
                {r.status === "approved" && (<>
                  <button onClick={() => confirmPickup(r.id)} className="btn-ghost flex items-center gap-1.5" style={{borderColor:"#22c55e",color:"#22c55e"}} data-testid={`owner-pickup-${r.id}`}><LogIn className="w-3.5 h-3.5" /> Masina predata</button>
                  <button onClick={() => { setExtendFor(r); setNewEndDate(r.end_date); }} className="btn-ghost flex items-center gap-1.5" data-testid={`owner-extend-${r.id}`}><CalendarPlus className="w-3.5 h-3.5" /> Prelungeste</button>
                  <button onClick={() => { setShortenFor(r); setNewEndDate(r.end_date); }} className="btn-ghost flex items-center gap-1.5" data-testid={`owner-shorten-${r.id}`}><CalendarMinus className="w-3.5 h-3.5" /> Scurteaza</button>
                  <button onClick={() => setPhotoFor(r)} className="btn-ghost flex items-center gap-1.5" data-testid={`owner-photos-${r.id}`}><Camera className="w-3.5 h-3.5" /> Fotografii{r.handover_photos?.length ? ` (${r.handover_photos.length})` : ""}</button>
                  {(new Date().toISOString().slice(0,10) < r.start_date) && (
                    <button onClick={() => cancel(r.id)} className="btn-ghost flex items-center gap-1.5" style={{borderColor:"#ef4444",color:"#ef4444"}} data-testid={`owner-cancel-${r.id}`}><Ban className="w-3.5 h-3.5" /> Anuleaza</button>
                  )}
                </>)}
                {r.status === "in_progress" && (<>
                  <button onClick={() => confirmReturn(r.id)} className="btn-ghost flex items-center gap-1.5" style={{borderColor:"#22c55e",color:"#22c55e"}} data-testid={`owner-return-${r.id}`}><LogOut className="w-3.5 h-3.5" /> Inchiriere incheiata</button>
                  <button onClick={() => setPhotoFor(r)} className="btn-ghost flex items-center gap-1.5" data-testid={`owner-photos-${r.id}`}><Camera className="w-3.5 h-3.5" /> Fotografii{r.handover_photos?.length ? ` (${r.handover_photos.length})` : ""}</button>
                </>)}
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-center py-16 text-sm" style={{ color: "var(--brand-text-secondary)" }}>Nu exista rezervari</div>}
      </div>

      {extendFor && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3 sm:p-6" onClick={() => setExtendFor(null)}>
          <div className="card-surface p-5 sm:p-8 w-full max-w-md max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-2xl mb-2">Prelungeste rezervarea</h2>
            <p className="text-sm mb-6" style={{ color: "var(--brand-text-secondary)" }}>{extendFor.car_name} &middot; client: {extendFor.guest_name}</p>
            <div className="mb-2 text-sm">Data curenta sfarsit: <span style={{ color: "var(--brand-primary)" }}>{fmtDate(extendFor.end_date)}</span></div>
            <label className="label-eyebrow block mb-2 mt-4">Noua data sfarsit</label>
            <input type="date" className="brand-input mb-6" value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} min={extendFor.end_date} data-testid="owner-extend-date" />
            <div className="flex gap-3">
              <button onClick={submitExtend} className="btn-gold-filled flex-1" data-testid="owner-confirm-extend">Confirma</button>
              <button onClick={() => setExtendFor(null)} className="btn-ghost">Anuleaza</button>
            </div>
          </div>
        </div>
      )}
      {shortenFor && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3 sm:p-6" onClick={() => setShortenFor(null)}>
          <div className="card-surface p-5 sm:p-8 w-full max-w-md max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-2xl mb-2">Scurteaza rezervarea</h2>
            <p className="text-sm mb-6" style={{ color: "var(--brand-text-secondary)" }}>{shortenFor.car_name} &middot; client: {shortenFor.guest_name}</p>
            <div className="mb-2 text-sm">Data curenta sfarsit: <span style={{ color: "var(--brand-primary)" }}>{fmtDate(shortenFor.end_date)}</span></div>
            <label className="label-eyebrow block mb-2 mt-4">Noua data sfarsit (mai devreme)</label>
            <input type="date" className="brand-input mb-6" value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} min={shortenFor.start_date} max={shortenFor.end_date} data-testid="owner-shorten-date" />
            <div className="flex gap-3">
              <button onClick={submitShorten} className="btn-gold-filled flex-1" data-testid="owner-confirm-shorten">Confirma</button>
              <button onClick={() => setShortenFor(null)} className="btn-ghost">Anuleaza</button>
            </div>
          </div>
        </div>
      )}

      {photoFor && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-auto p-3 sm:p-8" onClick={() => setPhotoFor(null)}>
          <div className="card-surface p-5 sm:p-8 w-full max-w-2xl mt-4 sm:mt-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-serif text-2xl">Fotografii predare / preluare</h2>
                <p className="text-sm mt-1" style={{ color: "var(--brand-text-secondary)" }}>{photoFor.car_name} &middot; {photoFor.guest_name}</p>
              </div>
              <button onClick={() => setPhotoFor(null)} className="text-xl">×</button>
            </div>

            <label className="flex flex-col items-center justify-center gap-3 p-6 rounded border-2 border-dashed cursor-pointer mb-6 transition hover:border-[var(--brand-primary)]" style={{ borderColor: "var(--brand-border)" }}>
              {uploading ? (
                <span className="text-sm" style={{ color: "var(--brand-text-secondary)" }}>Se incarca...</span>
              ) : (
                <>
                  <Camera className="w-8 h-8" style={{ color: "var(--brand-text-secondary)" }} />
                  <span className="text-sm" style={{ color: "var(--brand-text-secondary)" }}>Apasa pentru a selecta fotografii (JPG, PNG, WEBP)</span>
                </>
              )}
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                multiple
                className="hidden"
                disabled={uploading}
                data-testid="owner-photo-input"
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  if (!files.length) return;
                  setUploading(true);
                  try {
                    for (const file of files) {
                      const fd = new FormData();
                      fd.append("file", file);
                      fd.append("reservation_id", photoFor.id);
                      await api.post("/owner/inspections/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    }
                    const updated = (await api.get("/owner/reservations")).data;
                    setItems(updated);
                    const refreshed = updated.find((x) => x.id === photoFor.id);
                    if (refreshed) setPhotoFor(refreshed);
                  } catch (err) { alert(formatApiError(err)); }
                  finally { setUploading(false); e.target.value = ""; }
                }}
              />
            </label>

            {(photoFor.handover_photos || []).length > 0 ? (
              <div>
                <div className="label-eyebrow mb-3">Fotografii incarcate ({photoFor.handover_photos.length})</div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {photoFor.handover_photos.map((p, i) => (
                    <a key={i} href={resolveUrl(p.url)} target="_blank" rel="noopener noreferrer" className="aspect-square overflow-hidden rounded block" data-testid={`owner-photo-${i}`}>
                      <img src={resolveUrl(p.url)} alt="" className="w-full h-full object-cover hover:opacity-80 transition" />
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-sm flex items-center justify-center gap-2" style={{ color: "var(--brand-text-secondary)" }}>
                <ImageIcon className="w-4 h-4" /> Nicio fotografie incarcata inca
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// OWNER DOCUMENTS — manage ITP / RCA / rovinieta / CASCO per own car
// ============================================================================

const DOC_TYPE_LABELS = {
  rca: "RCA (asigurare obligatorie)",
  itp: "ITP (inspectie tehnica)",
  rovigneta: "Rovinieta",
  casco: "CASCO",
};
const STATUS_STYLES = {
  expired: { bg: "#7f1d1d", text: "#fef2f2", label: "EXPIRAT" },
  critical: { bg: "#9a3412", text: "#fff7ed", label: "EXPIRA CURAND" },
  warning: { bg: "#a16207", text: "#fefce8", label: "ATENTIE" },
  ok: { bg: "var(--brand-surface-elevated)", text: "var(--brand-text)", label: "OK" },
};

function OwnerDocuments() {
  const [cars, setCars] = useState([]);
  const [docsByCar, setDocsByCar] = useState({});
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [adminDocTypes, setAdminDocTypes] = useState([]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [carsRes, expRes] = await Promise.all([
        api.get("/owner/cars"),
        api.get("/owner/documents/expiring", { params: { days_ahead: 365 } }),
      ]);
      setCars(carsRes.data);
      setExpiring(expRes.data);
      try {
        const s = await api.get("/settings");
        setAdminDocTypes(s.data?.document_types || []);
      } catch (_) {}
      const map = {};
      await Promise.all(carsRes.data.map(async (c) => {
        const r = await api.get(`/owner/cars/${c.id}/documents`);
        map[c.id] = r.data;
      }));
      setDocsByCar(map);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  const docTypes = (adminDocTypes && adminDocTypes.length > 0)
    ? adminDocTypes
    : [
        { key: "rca", label_ro: "RCA", label_en: "Insurance (RCA)" },
        { key: "itp", label_ro: "ITP", label_en: "Technical inspection (ITP)" },
        { key: "rovigneta", label_ro: "Rovinieta", label_en: "Vignette" },
        { key: "casco", label_ro: "CASCO", label_en: "CASCO insurance" },
      ];

  const startAdd = (car_id, doc_type) => setEditing({ car_id, doc_type, expires_at: "", notes: "" });
  const startEdit = (car_id, d) => setEditing({ car_id, doc_id: d.id, doc_type: d.doc_type, expires_at: d.expires_at, notes: d.notes || "" });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.expires_at) { alert("Selecteaza data expirarii"); return; }
    try {
      const payload = {
        doc_type: editing.doc_type,
        expires_at: editing.expires_at,
        notes: editing.notes || "",
      };
      if (editing.doc_id) {
        await api.put(`/owner/cars/${editing.car_id}/documents/${editing.doc_id}`, payload);
      } else {
        await api.post(`/owner/cars/${editing.car_id}/documents`, payload);
      }
      setEditing(null);
      await loadAll();
    } catch (e) { alert(formatApiError(e)); }
  };

  const del = async (car_id, doc_id) => {
    if (!window.confirm("Stergi acest document?")) return;
    try {
      await api.delete(`/owner/cars/${car_id}/documents/${doc_id}`);
      await loadAll();
    } catch (e) { alert(formatApiError(e)); }
  };

  const statusFor = (expires_at) => {
    if (!expires_at) return "ok";
    const today = new Date().toISOString().slice(0, 10);
    const d = Math.round((new Date(expires_at) - new Date(today)) / 86400000);
    if (d < 0) return "expired";
    if (d <= 7) return "critical";
    if (d <= 30) return "warning";
    return "ok";
  };

  return (
    <div data-testid="owner-documents">
      <div className="mb-8">
        <div className="label-eyebrow mb-2" style={{ color: "var(--brand-primary)" }}>Proprietar</div>
        <h1 className="font-serif text-3xl sm:text-4xl">Documente masini</h1>
        <p className="text-sm mt-2" style={{ color: "var(--brand-text-secondary)" }}>
          Gestioneaza ITP, RCA, rovinieta si CASCO pentru masinile tale. Vei primi alerte automate inainte de expirare.
        </p>
      </div>

      {expiring.filter((d) => d.status !== "ok").length > 0 && (
        <div className="card-surface p-4 mb-6 flex items-start gap-3" style={{ borderLeft: "3px solid #ef4444" }} data-testid="owner-docs-alert">
          <AlertTriangle className="w-5 h-5 mt-0.5" style={{ color: "#ef4444" }} />
          <div className="flex-1">
            <div className="font-medium mb-1">Atentie — {expiring.filter((d) => d.status !== "ok").length} document(e) necesita actiune</div>
            <ul className="text-xs space-y-0.5" style={{ color: "var(--brand-text-secondary)" }}>
              {expiring.filter((d) => d.status !== "ok").slice(0, 5).map((d) => (
                <li key={d.id}>
                  <b>{d.car_name}</b> — {DOC_TYPE_LABELS[d.doc_type] || d.doc_type}: {STATUS_STYLES[d.status]?.label}
                  {d.days_until >= 0 ? ` (${d.days_until} zile)` : ` (acum ${Math.abs(d.days_until)} zile)`}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {loading && <div className="text-sm" style={{ color: "var(--brand-text-secondary)" }}>Se incarca...</div>}

      <div className="space-y-6">
        {cars.map((car) => {
          const docs = docsByCar[car.id] || [];
          const byType = Object.fromEntries(docs.map((d) => [d.doc_type, d]));
          return (
            <div key={car.id} className="card-surface p-5 sm:p-6" data-testid={`owner-docs-car-${car.id}`}>
              <div className="flex items-center gap-4 mb-4">
                {car.images?.[0] && <img src={resolveUrl(car.images[0].url)} alt="" className="w-16 h-12 object-cover rounded" />}
                <div>
                  <h3 className="font-serif text-lg">{car.name}</h3>
                  <p className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>{car.brand} {car.model}</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {docTypes.map((dt) => {
                  const d = byType[dt.key];
                  const status = d ? statusFor(d.expires_at) : "ok";
                  const isEditing = editing && editing.car_id === car.id && editing.doc_type === dt.key;
                  return (
                    <div key={dt.key} className="rounded border p-3 text-xs" style={{ borderColor: "var(--brand-border)", background: d ? STATUS_STYLES[status].bg : "transparent", color: d ? STATUS_STYLES[status].text : "var(--brand-text)" }} data-testid={`owner-doc-${car.id}-${dt.key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> {dt.label_ro || dt.label_en || dt.key}</div>
                        {d && <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/30">{STATUS_STYLES[status].label}</span>}
                      </div>
                      {isEditing ? (
                        <div className="space-y-2">
                          <input type="date" className="brand-input text-xs py-1.5 w-full" value={editing.expires_at} onChange={(e) => setEditing({...editing, expires_at: e.target.value})} data-testid={`owner-doc-date-${car.id}-${dt.key}`} />
                          <input type="text" className="brand-input text-xs py-1.5 w-full" placeholder="Notite (optional)" value={editing.notes} onChange={(e) => setEditing({...editing, notes: e.target.value})} />
                          <div className="flex gap-1">
                            <button onClick={save} className="btn-gold-filled text-xs px-2 py-1 flex-1 inline-flex items-center justify-center gap-1" data-testid={`owner-doc-save-${car.id}-${dt.key}`}><Check className="w-3 h-3" /> Salveaza</button>
                            <button onClick={cancel} className="btn-ghost text-xs px-2 py-1"><X className="w-3 h-3" /></button>
                          </div>
                        </div>
                      ) : d ? (
                        <div>
                          <div>Expira: <b>{d.expires_at}</b></div>
                          {d.notes && <div className="opacity-80 mt-0.5">{d.notes}</div>}
                          <div className="flex gap-1 mt-2">
                            <button onClick={() => startEdit(car.id, d)} className="btn-ghost text-xs px-2 py-1 flex-1 inline-flex items-center justify-center gap-1" data-testid={`owner-doc-edit-${car.id}-${dt.key}`}><Pencil className="w-3 h-3" /> Editeaza</button>
                            <button onClick={() => del(car.id, d.id)} className="btn-ghost text-xs px-2 py-1" data-testid={`owner-doc-delete-${car.id}-${dt.key}`}><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => startAdd(car.id, dt.key)} className="btn-ghost text-xs w-full py-1 inline-flex items-center justify-center gap-1" data-testid={`owner-doc-add-${car.id}-${dt.key}`}>
                          <Plus className="w-3 h-3" /> Adauga
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {!loading && cars.length === 0 && (
          <div className="text-center py-16 text-sm" style={{ color: "var(--brand-text-secondary)" }}>Nu ai masini asignate inca.</div>
        )}
      </div>
    </div>
  );
}
